import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { NgxMaskDirective } from 'ngx-mask';
import { take } from 'rxjs/operators';
import { AppFacade } from '@appShared/services/app.facade';
import { AdminService } from '@appShared/services/admin.service';
import { CommonService } from '@appShared/services/common.service';
import { ButtonComponent } from '@appShared/components/button/button.component';
import {
   ConfirmModalComponent,
   ConfirmService,
   ConfirmTemplateDirective
} from '@appShared/components/confirm-modal-and-service';
import {
  ShareholderEditSectionData,
  ShareholderEditSection_
} from '@appShared/services/admin.service';
import { ToastrType } from '@appShared/services/toastr.service';
import {
  IDividend,
  IShareholder
} from '@appShared/interfaces/[Model-based]';
import { IDomainModel } from '@appShared/services/lookup/[CodeGen]/domain-model.interface';

@Component({
  selector: 'app-shareholder-dividend',
   templateUrl: './shareholder-dividend.component.html',
   imports: [
      ReactiveFormsModule,
      CurrencyPipe,
      NgSelectComponent,
      NgOptionComponent,
      NgxMaskDirective,
      ButtonComponent,
      ConfirmTemplateDirective,
      ConfirmModalComponent
   ]
})
export class ShareholderDividendComponent implements OnInit, OnChanges {
  @Input() shareholder: IShareholder;
  @Input() canEdit: boolean;
  @Output() shareholderSectionUpdated = new EventEmitter();

  dividends: IDividend[];
  selectedDividend: IDividend;
  isLoading: boolean;
  isDeleting: boolean;

  /* form/form-fields */
  dividendForm: FormGroup;
  private _stockPlanBeginingYear: number;
  private _currentYear = new Date().getFullYear();
  yearDropdown: FormControl;
  years: IDomainModel[] = [];
  amountPaid: FormControl;
  mouseoverDividendSubmit: boolean;
  isSubmitting: boolean;

  constructor(
    private _appFacade: AppFacade,
    private _adminService: AdminService,
    private _confirmService: ConfirmService,
    private _commonService: CommonService
  ) { }

  ngOnInit(): void {
    this.isLoading = true;

    this._getDividends();

    this._appFacade.siteConfig$.pipe(take(1)).subscribe(siteConfig => {
      this._stockPlanBeginingYear = siteConfig.stockPlanBeginingYear;
      this._createFormGroup();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes?.['shareholder']?.isFirstChange()) {
      this._setDividendData();
    }
  }

  /*
   * events
   * */

  onYearChange(year: IDomainModel): void {
    this._adminService
      .getDividendByYear(this.shareholder.id, year.code)
      .then(existingDividend => {
        this._setDividendData(existingDividend, year.code);
      })
      .catch(() => {
        /* Do Nothing */
      })
      .finally(() => (this.isLoading = false));
  }

  /*
   * private methods
   * */

  private _getDividends(): void {
    this._adminService
      .getDividends(this.shareholder.id)
      .then(dividends => {
        this.dividends = dividends;
      })
      .catch(() => {
        /* Do Nothing */
      })
      .finally(() => (this.isLoading = false));
  }

  private _createFormGroup(): void {
    for (
      let i = this._stockPlanBeginingYear;
      i <= this._currentYear;
      i++
    ) {
      this.years.push({ code: i, description: i.toString() } as IDomainModel);
    }

    this.yearDropdown = new FormControl(null, Validators.required);
    this.amountPaid = new FormControl(null, Validators.required);

    this.dividendForm = new FormGroup({
      yearDropdown: this.yearDropdown,
      amountPaid: this.amountPaid
    });

    this._setDividendData();
  }

  private _setDividendData(selectedDividend?: IDividend, incomingYear?: number): void {
    let year;

    selectedDividend =
      selectedDividend ||
      ({ id: 0, shareholderId: this.shareholder.id, year: incomingYear } as IDividend);

    if (selectedDividend.year) {
      year = this.years.find(yr => selectedDividend.year === yr.code);
    }

    this.dividendForm.patchValue({
      yearDropdown: year || this.years[this.years.length - 1],
      amountPaid: this._commonService.setNumber(selectedDividend.amountPaid)
    });

    this.selectedDividend = selectedDividend;

    selectedDividend.id ? this.yearDropdown.disable() : this.yearDropdown.enable();

    this.canEdit ? this.dividendForm.enable() : this.dividendForm.disable();
  }

  /*
   * public methods
   * */

  editDividend(dividend?: IDividend): void {
    if (dividend?.id || (this.shareholder.id && !this.shareholder.repurchaseOptionAsOf)) {

      this._setDividendData(dividend);

    } else if (!this.shareholder.id) {
      this._commonService.messageUser(
        `You somehow got to this page without choosing
          a shareholder. Unable to Add Plan`,
        'Error Occurred!',
        ToastrType.error
      );
    } else {
      this._commonService.messageUser(
        `${this.shareholder.info.displayName} has
        a repurchase option term commencement date.<p>Correct this issue before proceeding.</p>`,
        'Repurchase Option!',
        ToastrType.error
      );
    }
  }

  saveDividend(): void {
    this.isSubmitting = true;

    const dividendFormValues = this.dividendForm.value;
    const updatedDividend = {
      ...this.selectedDividend,
      year: dividendFormValues.yearDropdown?.code,
      amountPaid: dividendFormValues.amountPaid
    } as IDividend;

    this._adminService
      .createUpdateDividend(updatedDividend)
      .then(shareholder => {
        const msgSuffix = updatedDividend.id ? 'updated' : 'created';
        this._commonService.messageUser(`Dividend ${msgSuffix}!`);

        this._setDividendData();
        this._getDividends();

        this.dividendForm.markAsPristine();
        this.dividendForm.markAsUntouched();

        this.shareholderSectionUpdated.emit({
          shareholderEditSection: ShareholderEditSection_.dividendInfo,
          shareholder,
          refresh: true
        } as ShareholderEditSectionData);
      })
      .catch(() => {
        /* Do Nothing */
      })
      .finally(() => setTimeout(() => (this.isSubmitting = false), 500));
  }

  deleteDividend(dividend: IDividend): void {
    this._confirmService
      .confirm({
        title: 'Deleting Dividend!',
        message: `You are deleting the ${dividend.year} dividend for:
            <p class='ps-3'><strong class='text-site-primary'>
              ${this.shareholder.info.displayName}
            </strong><p>
            Are you sure?`
      })
      .then(() => {
        this.isDeleting = true;

        this._adminService
          .deleteDividend(dividend)
          .then(() => {
            this._commonService.messageUser('Dividend deleted!');

            this._getDividends();
            this._setDividendData();

            this.shareholderSectionUpdated.emit({
              shareholderEditSection: ShareholderEditSection_.newEmployeeInfo,
              shareholder: this.shareholder,
              refresh: true
            } as ShareholderEditSectionData);
          })
          .catch(() => {
            /* Do Nothing */
          })
          .finally(() => setTimeout(() => (this.isDeleting = false), 500));
      })
      .catch(() => {
        /* Do Nothing */
      });
  }

  clearSelectedDividend(): void {
    this._setDividendData();
  }
}
